import React from 'react';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Check, Minus } from 'lucide-react';
import { BookingDetailsAdditionalService } from '../../../../../store/slices/bookingCreationSlice';
import { useTranslation } from 'react-i18next';
import { getCurrentLang } from '../../../../../i18n/i18n';
import { formatCurrency } from '../../../../../utils';
import CurrentPriceHint from './CurrentPriceHint';

type Props = {
    service: BookingDetailsAdditionalService;
    isChecked: boolean;
    // Whether the service allows choosing a quantity (asset-level flag), not the selected count.
    isQuantitySelectable: boolean;
    // The service's price in the catalog today. Rendered beside the booked price
    // when the two differ (#1735); omit when the service is no longer catalogued.
    currentPrice?: number | null;
};

const BookingDetailsServiceItem: React.FC<Props> = ({
    service,
    isChecked,
    isQuantitySelectable,
    currentPrice,
}) => {
    const { t } = useTranslation();
    const name = service.langNames?.[getCurrentLang()] || service.serviceName;
    const selectedQuantity = service.quantity ?? 0;
    const showQuantity = isChecked && isQuantitySelectable && selectedQuantity > 0;

    return (
        <Box className={'flex items-center gap-2 h-[30px]'}>
            <span className={'flex items-center justify-center w-4 h-4 shrink-0'}>
                {isChecked ? (
                    <Check size={16} className={'text-foreground/70'} />
                ) : (
                    <Minus size={14} className={'text-foreground/30'} />
                )}
            </span>
            <Typography className={`text-sm ${isChecked ? 'text-foreground/80' : 'text-foreground/40'}`}>
                {name}
                {showQuantity && (
                    <span className={'text-foreground/60'}>
                        {' '}· {t('booking_details.quantity')} {selectedQuantity}
                    </span>
                )}
                {service.unitPrice === 0 ? (
                    <span className={'text-foreground/60'}> | {t('booking_details.free')}</span>
                ) : (
                    <span className={'text-foreground/60'}>
                        {' '}| {formatCurrency(service.unitPrice, getCurrentLang())}
                        {(service as { priceUnit?: string | null }).priceUnit
                            ? ` ${t('price_unit.' + (service as { priceUnit?: string | null }).priceUnit)}`
                            : ''}
                        {isQuantitySelectable ? ` ${t('booking_details.per_unit')}` : ''}
                    </span>
                )}
                <CurrentPriceHint bookedPrice={service.unitPrice} currentPrice={currentPrice} />
            </Typography>
        </Box>
    );
};

export default BookingDetailsServiceItem;
